"use client";

import { c as _c } from "react/compiler-runtime";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type CSSProperties } from "react";
import type { Swiper as SwiperInstance } from "swiper";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import CarouselArrow from "./CarouselArrow";
import SubmissionArtworkCard from "./SubmissionArtworkCard";
export interface SubmissionCarouselHandle {
  pauseAutoplay: () => void;
  resumeAutoplay: () => void;
}
interface SubmissionCarouselProps {
  readonly onActiveDropChange?: (drop: ExtendedDrop | null) => void;
}
const SubmissionCarousel = forwardRef<SubmissionCarouselHandle, SubmissionCarouselProps>(function SubmissionCarousel(t0, ref) {
  const $ = _c(45);
  const {
    onActiveDropChange
  } = t0;
  const {
    seizeSettings,
    isLoaded
  } = useSeizeSettings();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const waveId = seizeSettings.memes_wave_id;
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const t1 = waveId ?? "";
  const t2 = !waveId;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      waveId: t1,
      sort: WaveDropsLeaderboardSort.RATING_PREDICTION,
      pausePolling: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    drops,
    isFetching
  } = useWaveDropsLeaderboard(t3);
  const swiperRef = useRef(null);
  let t4;
  let t5;
  if ($[3] !== hasTouchScreen) {
    t4 = () => ({
      pauseAutoplay: () => {
        if (hasTouchScreen) {
          return;
        }
        swiperRef.current?.autoplay?.stop?.();
      },
      resumeAutoplay: () => {
        if (hasTouchScreen) {
          return;
        }
        swiperRef.current?.autoplay?.start?.();
      }
    });
    t5 = [hasTouchScreen];
    $[3] = hasTouchScreen;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useImperativeHandle(ref, t4, t5);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      "--carousel-inactive-scale": "0.68",
      "--carousel-gap": "4px",
      "--carousel-arrow-offset": "calc((var(--carousel-card-width) / 2) + var(--carousel-gap) + (var(--carousel-card-width) * var(--carousel-inactive-scale) / 2))"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const carouselVars = t6 as CSSProperties;
  let t7;
  if ($[7] !== hasTouchScreen) {
    t7 = hasTouchScreen ? false : {
      delay: 4000,
      disableOnInteraction: true
    };
    $[7] = hasTouchScreen;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const autoplayConfig = t7;
  let t8;
  if ($[9] !== drops) {
    const filtered = drops.filter(_temp);
    const hashCode = _temp2;
    let t9;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = (a, b) => hashCode(a.id) - hashCode(b.id);
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    t8 = [...filtered].sort(t9);
    $[9] = drops;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const shuffledDrops = t8;
  const activeDrop = shuffledDrops[activeIndex] ?? shuffledDrops[0] ?? null;
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx600;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = swiper => {
      setActiveIndex(swiper.activeIndex);
      setCanScrollLeft(!swiper.isBeginning);
      setCanScrollRight(!swiper.isEnd);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleSlideChange = t9;
  let t10;
  let t11;
  if ($[13] !== activeDrop || $[14] !== onActiveDropChange) {
    t10 = () => {
      onActiveDropChange?.(activeDrop);
    };
    t11 = [activeDrop, onActiveDropChange];
    $[13] = activeDrop;
    $[14] = onActiveDropChange;
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[17] !== activeIndex || $[18] !== shuffledDrops.length) {
    t12 = () => {
      if (activeIndex >= shuffledDrops.length && shuffledDrops.length > 0) {
        setActiveIndex(0);
        swiperRef.current?.slideTo(0, 0);
      }
    };
    t13 = [activeIndex, shuffledDrops.length];
    $[17] = activeIndex;
    $[18] = shuffledDrops.length;
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[21] !== hasTouchScreen) {
    t14 = () => {
      if (hasTouchScreen) {
        swiperRef.current?.autoplay?.stop?.();
      }
    };
    t15 = [hasTouchScreen];
    $[21] = hasTouchScreen;
    $[22] = t14;
    $[23] = t15;
  } else {
    t14 = $[22];
    t15 = $[23];
  }
  useEffect(t14, t15);
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = direction => {
      const swiper_0 = swiperRef.current;
      if (!swiper_0) {
        return;
      }
      if (direction === "left") {
        swiper_0.slidePrev();
      } else {
        swiper_0.slideNext();
      }
    };
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const scroll = t16;
  if (!isLoaded || !waveId) {
    return null;
  }
  if (isFetching && shuffledDrops.length === 0) {
    let t17;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div className="tw-flex tw-h-64 tw-items-center tw-justify-center"><div className="tw-text-sm tw-text-iron-500">Loading...</div></div>;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    return t17;
  }
  if (shuffledDrops.length === 0) {
    return null;
  }
  let t17;
  if ($[26] !== canScrollLeft) {
    t17 = true && <CarouselArrow direction="left" onClick={() => scroll("left")} disabled={!canScrollLeft} style={{
      left: "var(--arrow-left-pos)"
    }} className="[--arrow-left-pos:2rem] md:[--arrow-left-pos:calc(50%-var(--carousel-arrow-offset))]" />;
    $[26] = canScrollLeft;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [Autoplay];
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== hasTouchScreen) {
    t19 = swiper_1 => {
      swiperRef.current = swiper_1;
      handleSlideChange(swiper_1);
      if (hasTouchScreen) {
        swiper_1.autoplay?.stop?.();
      }
    };
    $[29] = hasTouchScreen;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== activeIndex || $[32] !== mediaImageScale || $[33] !== shuffledDrops) {
    t20 = shuffledDrops.map((drop_0, index) => {
      const isActive = index === activeIndex;
      const distance = Math.abs(index - activeIndex);
      let renderMode = "placeholder";
      if (distance === 0) {
        renderMode = "full";
      } else {
        if (distance === 1) {
          renderMode = "preview";
        }
      }
      const previewFilter = renderMode === "preview" ? "grayscale(100%) blur(3px)" : "none";
      return <SwiperSlide key={drop_0.id} className="tw-flex tw-h-full tw-items-center tw-justify-center tw-overflow-visible" style={{
        width: "var(--carousel-card-width)"
      }}><div role="button" tabIndex={0} onClick={() => swiperRef.current?.slideTo(index)} onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            swiperRef.current?.slideTo(index);
          }
        }} className="tw-h-auto tw-w-full tw-aspect-[4/5] tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400 md:tw-aspect-auto md:tw-h-full" style={{
          opacity: isActive ? 1 : 0.22,
          filter: isActive ? "none" : previewFilter,
          zIndex: isActive ? 10 : 10 - distance,
          transition: "transform 0.45s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.45s cubic-bezier(0.25, 1, 0.5, 1)",
          transform: isActive ? "translate3d(0, -8px, 0) scale(1)" : "translate3d(0, 0, 0) scale(0.68)",
          transformOrigin: "center",
          willChange: distance <= 1 ? "transform, opacity" : "auto"
        }}><SubmissionArtworkCard drop={drop_0} isActive={isActive} imageScale={mediaImageScale} renderMode={renderMode} /></div></SwiperSlide>;
    });
    $[31] = activeIndex;
    $[32] = mediaImageScale;
    $[33] = shuffledDrops;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== autoplayConfig || $[36] !== t19 || $[37] !== t20) {
    t21 = <Swiper className="submission-carousel-swiper tw-h-full tw-overflow-hidden tw-touch-pan-y" modules={t18} slidesPerView="auto" centeredSlides={true} spaceBetween={4} speed={450} initialSlide={0} autoplay={autoplayConfig} touchEventsTarget="container" touchStartPreventDefault={false} touchMoveStopPropagation={false} touchReleaseOnEdges={true} preventInteractionOnTransition={true} onSwiper={t19} onSlideChange={handleSlideChange}>{t20}</Swiper>;
    $[35] = autoplayConfig;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== canScrollRight) {
    t22 = true && <CarouselArrow direction="right" onClick={() => scroll("right")} disabled={!canScrollRight} style={{
      left: "var(--arrow-right-pos)",
      right: "var(--arrow-right-r)"
    }} className="[--arrow-right-pos:auto] [--arrow-right-r:-0.5rem] md:[--arrow-right-pos:calc(50%+var(--carousel-arrow-offset))] md:[--arrow-right-r:auto]" />;
    $[39] = canScrollRight;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t17 || $[42] !== t21 || $[43] !== t22) {
    t23 = <div className="tw-relative tw-h-full tw-min-h-0 tw-@container [--carousel-card-width:clamp(220px,80%,440px)] md:[--carousel-card-width:clamp(160px,50%,440px)]" style={carouselVars}><div className="tw-mx-auto tw-flex tw-h-full tw-w-full tw-flex-col"><div className="tw-relative tw-min-h-0 tw-flex-1 tw-overflow-hidden">{t17}{t21}{t22}</div></div></div>;
    $[41] = t17;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  return t23;
});
export default memo(SubmissionCarousel);
function _temp(drop) {
  return (drop.parts[0]?.media.length ?? 0) > 0;
}
function _temp2(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash | 0;
  }
  return hash;
}